
import './record-edit-screen.scss'

import { defineComponent } from 'vue'
import { TranslateResult } from 'vue-i18n'
import _ from 'lodash'
import { set } from 'lodash'
import { empty } from 'rxjs'
import { catchError, first, switchMap, tap } from 'rxjs/operators'
import { faArrowLeft, faEye, faTabletAlt } from '@fortawesome/free-solid-svg-icons'

import { AuditCampaign, RecordMaskResource, Item, AuditCampaignFeatured, SearchFeatured, Record, RecordFeatured, EntityFieldStatus, EntityField, SpecimenResource } from '@skinsoft/types'
import { AuditRoutes } from '@skinsoft/const'
import { auditRecord_recordMask } from '@skinsoft/record-mask-configs'
import { itemSelectors, locationSelectors, recordSelectors, specimenSelectors } from '@skinsoft/selectors'
import { toCampaignFeatured$, toRecordFeatured$ } from '@skinsoft/entity-ui'

import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { MaskFieldUpdateEventType } from '@skinsoft/record-mask/src/components/mask-field'
import { RecordMaskDisplay } from '@skinsoft/record-mask'
import { toSearchFeatured$ } from '@skinsoft/search-data-store/src/featured'
import { CollapsibleHeader } from '@skinsoft/ui'
import { Media } from '@skinsoft/ui/src/item-card/media'

import { FORCE_CLOSE_MENU_TOPIC, SAVE_CURRENT_RECORD_TOPIC } from '../../../busTopics'
import { TRANSITION_RECORD_TO_STATE } from '../../../events/events'
import { TransitionRecordToStateEvent } from '../../../events/types'
import { AuditItemEditScreen } from '../audit-item-edit-screen'
import { AddMediaEvent } from '@skinsoft/entity-ui/src/media/medias-fields-area'
import { DISPLAY_ALERT_TOPIC } from '@skinsoft/bus/src/busTopics'

declare const OFFLINE_DISABLED_MASK_FIELDS: { entityId: string, message?: string }[]

export const RecordEditScreen = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sCollapsibleHeader: CollapsibleHeader,
        sAuditItemEditScreen: AuditItemEditScreen,
        sMedia: Media,
        sRecordMaskDisplay: RecordMaskDisplay
    },
    props: ['recordId', 'campaignId'],
    template: require('./record-edit-screen.html'),
    data() {
        return {
            campaign: null as any as AuditCampaign,
            record: null as any as Record,
            resource: null as any,

            // Copy of the record used for saving, it avoids unnecessary rerender of components
            recordToSave: null as any as Record,

            campaignFeatured: null as any as AuditCampaignFeatured,
            recordFeatured: null as any as RecordFeatured,
            itemFeatured: null as any as SearchFeatured,

            itemLocation: "",
            displayItemScreen: false,
            overrideFromUpdateCache: false,

            recordMask: auditRecord_recordMask as RecordMaskResource,

            offlineDisabledMaskFields: undefined as { entityId: string, message?: string }[] | undefined,
            isOnline: window.navigator.onLine,
            fieldsErrors: [] as { entityId: string, message: string }[],

            // Icons
            backIcon: faArrowLeft,
            eyeIcon: faEye,
            localIcon: faTabletAlt,

            saveSuccessMessage: "",
            saveErrorMessage: "",
        }
    },
    computed: {
        fromUpdateCache(): boolean {
            return this.recordFeatured?.fromUpdateCache || this.overrideFromUpdateCache;
        },

        itemPath(): string | undefined {
            if (this.campaignFeatured && this.recordFeatured && this.itemFeatured) {
                return `${this.campaignFeatured.identificationNumber} > ${this.recordFeatured.recordIdentifier} > ${this.itemFeatured.inventoryNumber} `
            }
        },

        isReadOnly(): boolean {
            const recordState = recordSelectors.currentState(this.record, this.getAppLocale())
            return recordState === 'validated' || recordState === 'toValidated'
        },

        status(): TranslateResult {
            const translation = this.$t(`state.${this.recordFeatured.currentState}`)
            // if there is no translation (added to fix status in offline mode)
            if(translation === `state.${this.recordFeatured.currentState}`) {
                return this.recordFeatured.currentState
            }

            return translation
        },
    },
    mounted() {
        this.subscribeToBus(SAVE_CURRENT_RECORD_TOPIC, (event) => this.saveMediaAndRecord())
        this.subscribeToBus(TRANSITION_RECORD_TO_STATE, (event) => this.transitionRecord(event.payload))
        this.saveSuccessMessage = this.$t('editRecordScreen.notifications.saveSuccess') as string;
        this.saveErrorMessage = this.$t('editRecordScreen.notifications.saveError') as string;

        this.offlineDisabledMaskFields = OFFLINE_DISABLED_MASK_FIELDS.map(disabledMaskField => {
            if(disabledMaskField.message) {
                disabledMaskField.message = this.$t(disabledMaskField.message) as string
            }

            return disabledMaskField
        })

        // we use event listener's for connectivity status because it enable to have components that updates when the network status changes
        window.addEventListener('online', this.updateConnexionStatus)
        window.addEventListener('offline', this.updateConnexionStatus)
    },
    beforeUnmount() {
        window.removeEventListener('online', this.updateConnexionStatus)
        window.removeEventListener('offline', this.updateConnexionStatus)
    },
    methods: {
        updateConnexionStatus() {
            this.isOnline = window.navigator.onLine
        },

        saveMediaAndRecord() {
            this.saveRecord()
        },

        saveRecord() {
            this.$recordDataStore.update(this.recordId, this.recordToSave)
                .pipe(first(),
                    catchError(
                        err => {
                            const alertPayload = {
                                message: this.saveErrorMessage,
                                variant: "danger"
                            };
                            this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                            return empty()
                        }
                    ))
                .subscribe(
                    response => {
                        this.overrideFromUpdateCache = !!response.fromUpdateCache;

                        const alertPayload = {
                            message: this.saveSuccessMessage,
                            variant: "success"
                        };

                        this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                    }
                );
        },

        subscribe() {
            if (this.campaignId && this.recordId) {
                const locale = this.getAppLocale()
                this.subscribeTo(
                    this.$auditCampaignDataStore.fetchOne(this.campaignId).pipe(
                        switchMap(campaign => toCampaignFeatured$(campaign, locale))
                    ),
                    campaignFeatured => {
                        this.campaignFeatured = campaignFeatured;
                    },
                    'campaign-subscription')

                this.subscribeTo(
                    this.$recordDataStore.fetchOne(this.recordId).pipe(
                        tap(
                            record => {
                                this.record = record
                                this.recordToSave = _.cloneDeep(record);
                            }
                        ),
                        switchMap(record => {
                            return toRecordFeatured$(record, locale)
                        }),
                        switchMap( // Keeping record data
                            recordFeatured => {
                                this.recordFeatured = recordFeatured;
                                switch (this.recordFeatured.linkedItemType) {
                                    case "Specimen":
                                        return this.$specimenDataStore.fetchOne(recordFeatured.linkedItemId)
                                    default:
                                        return this.$itemDataStore.fetchOne(recordFeatured.linkedItemId)
                                }
                            }
                        ),
                        tap( // Fetching location
                            async linkedResource => {
                                let locId 
                                switch (linkedResource.type) {
                                    case 'specimen':
                                        locId = specimenSelectors.realTimeLocation(linkedResource as SpecimenResource)
                                        break
                                    default:
                                        locId = itemSelectors.realTimeLocation(linkedResource as Item)
                                }
                                if (locId) {
                                    const loc = await (this.$locationDataStore.fetchOne(locId).pipe(first()).toPromise());
                                    this.itemLocation = locationSelectors.translatedTitle(
                                        loc,
                                        this.getAppLocale()
                                    )
                                }
                            }
                        ),
                        switchMap(linkedResource => { // Keeping item data
                            this.resource = linkedResource as Item;
                            return toSearchFeatured$({
                                expression: null,
                                item: linkedResource as Item,
                                manifestation: null,
                                work: null,
                            }, locale, false)
                        })
                    ),
                    itemFeatured => {
                        this.itemFeatured = itemFeatured;
                    },
                    'record-subscription')
            }
        },

        unsubscribe() {
            this.unsubscribeFrom('record-subscription')
            this.unsubscribeFrom('campaign-subscription')
        },

        transitionRecord(event: TransitionRecordToStateEvent) {
            const errorMessage = this.$t('editRecordScreen.notifications.stateTransitionError');
            const sucessMessage = this.$t('editRecordScreen.notifications.stateTransitionSuccess');
            this.$recordDataStore.transitionRecord(this.record.id, event.state, event.translations)
                .then(
                    () => {
                        this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                            message: sucessMessage,
                            variant: "success"
                        })
                        this.goBack()
                    }
                ).catch(
                    () => {
                        this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                            message: errorMessage,
                            variant: "danger"
                        })
                    }
                )
        },

        openItemEditScreen() {
            this.saveMediaAndRecord();
            this.displayItemScreen = true
        },

        updateRecord(updateEvent) {
            if(updateEvent.type === MaskFieldUpdateEventType.DELETE) {
                // In case of a deletion, we need to check if the deleted value
                // is inside an array. Indeed, we have to use 2 differents approaches
                // depending if we delete a field or a value inside an array.

                const lastPropInPathRegex = /(\[\d\]|\.[a-zA-Z\d]*)$/

                const path = updateEvent.fieldPath
                const pathToLastParent = path.replace(lastPropInPathRegex, '')
                const lastParent = _.get(this.recordToSave, pathToLastParent)

                if(_.isArray(lastParent)) {
                    // if the last parent is an array, we extract the index of the value
                    // to delete from the path (it is the last "prop" in the path)

                    const indexMatches = path.match(lastPropInPathRegex)
                    if(indexMatches?.length) {
                        // @ts-ignore need to upgrade ts (replaceAll)
                        const indexString = indexMatches[0].replaceAll(/\[|\]|\./g, '')
                        const index = parseInt(indexString)
                        if(!isNaN(index)) {
                            lastParent.splice(index, 1)
                        } else {
                            // in case of error the user will see no action from his deletion, so no need
                            // to show an error message
                        }

                    } else {
                        // in case of error the user will see no action from his deletion, so no need
                        // to show an error message
                    }

                } else {
                    _.unset(this.recordToSave, path)
                }
            } else {
                // update the field with event data
                set(this.recordToSave, updateEvent.fieldPath, updateEvent.value)
            }
        },

        goBack() {
            this.$router.push({ name: AuditRoutes.CAMPAIGN, params: { campaignId: this.campaignId } });
            this.emitOnBus(FORCE_CLOSE_MENU_TOPIC)
        },

        onAddMedia(addedMedia: AddMediaEvent){
            const medias = _.get(this.recordToSave, 'associatedMedias.medias', null)

            if(addedMedia.newMediaId) {
                if(medias === null){
                    _.set(this.recordToSave, 'associatedMedias.medias', [addedMedia.newMediaId])
                }else{
                    const mediasWithoutCachedVersion = medias.filter(mediaId => mediaId !== addedMedia.cacheKey)
                    _.set(this.recordToSave, 'associatedMedias.medias', mediasWithoutCachedVersion)
                    _.get(this.recordToSave, 'associatedMedias.medias', [] as string[]).push(addedMedia.newMediaId)
                }

            } else if(!medias?.includes(addedMedia.cacheKey)) {
                if(medias === null){
                    _.set(this.recordToSave, 'associatedMedias.medias', [addedMedia.cacheKey])
                }else{
                    _.get(this.recordToSave, 'associatedMedias.medias', [] as string[]).push(addedMedia.cacheKey)
                }
            }
        },

        getEntityFieldStatus(entityField: EntityField): EntityFieldStatus {
            let result: EntityFieldStatus = {
                disabled: false,
                error: false
            }

            if(!this.isOnline) {
                const disabledEntityField = this.offlineDisabledMaskFields?.find(disabledField => {
                    return disabledField.entityId === (entityField as any).entityFieldId
                    // id is not always present on entityField but entityFieldId is always
                })

                if(disabledEntityField) {
                    result.disabled = true
                    if(disabledEntityField.message) {
                        result.disabledMessage = disabledEntityField.message
                    }
                }
            }

            const errorEntityField = this.fieldsErrors?.find(error => {
                return error.entityId === (entityField as any).entityFieldId
            })

            if(errorEntityField) {
                result.error = true
                result.errorMessage = errorEntityField.message
            }

            return result
        },
    },
    watch: {
        '$route': {
            handler() {
                this.unsubscribe();
                this.subscribe();
            },
            immediate: true,
        },
    },
})
